import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { AuthRequest } from '../../dtos/auth-request';
import { AdminUserService } from '../../services/admin-user.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  loginForm: UntypedFormGroup;
  // After first submission attempt, form validation will start
  submitted = false;
  // Error flag
  error = false;
  errorMessage: any = {};
  showSuperAdminSetupLink = false;
  private returnUrl: string | null = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private adminUserService: AdminUserService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  /**
   * Form validation will start after the method is called, additionally an AuthRequest will be sent
   */
  loginUser() {
    this.submitted = true;
    if (this.loginForm.valid) {
      const authRequest: AuthRequest = new AuthRequest(
        this.loginForm.controls.username.value,
        this.loginForm.controls.password.value,
      );
      this.authenticateUser(authRequest);
    } else {
      console.log('Invalid input');
    }
  }

  /**
   * Send authentication data to the authService. If the authentication was successfully, the user will be forwarded to the message page
   *
   * @param authRequest authentication data from the user login form
   */
  authenticateUser(authRequest: AuthRequest) {
    console.log('Try to authenticate user: ' + authRequest.email);
    this.authService.loginUser(authRequest).subscribe({
      next: () => {
        console.log('Successfully logged in user: ' + authRequest.email);
        this.toastService.success(
          'Welcome back!',
          `Successfully logged in as ${authRequest.email}`,
        );
        const role = this.authService.getUserRole();
        const target =
          this.returnUrl || (['ADMIN', 'SUPER_ADMIN'].includes(role) ? '/profile' : '/');
        // reset returnUrl to avoid reuse
        this.returnUrl = null;
        this.router.navigateByUrl(target);
      },
      error: (error) => {
        console.log('Could not log in due to:');
        console.log(error);
        this.error = true;
        this.errorMessage = this.parseLoginError(error);
        this.toastService.error(
          'Login Failed',
          this.errorMessage.detail || 'Invalid email or password',
        );
      },
    });
  }

  /**
   * Turns an HttpErrorResponse into a `{ detail }` object for the banner without ever throwing.
   * Distinguishes an unreachable backend (status 0) from a credential/validation error, and
   * tolerates non-JSON error bodies (the previous bare JSON.parse threw on those).
   */
  private parseLoginError(error: any): { detail: string } {
    if (error?.status === 0) {
      return { detail: 'Cannot reach the server. Please check your connection and try again.' };
    }
    const body = error?.error;
    const raw = body && typeof body === 'object' ? body.error : body;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return { detail: raw };
      }
    }
    if (body && typeof body === 'object' && typeof body.detail === 'string') {
      return body;
    }
    return { detail: 'Invalid email or password' };
  }

  /**
   * Error flag will be deactivated, which clears the error message
   */
  vanishError() {
    this.error = false;
  }

  ngOnInit() {
    // read returnUrl from query params (set by the AuthGuard)
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.loadSuperAdminSetupAvailability();
  }

  private loadSuperAdminSetupAvailability(): void {
    this.adminUserService.isSuperAdminSetupAvailable().subscribe({
      next: (available) => {
        this.showSuperAdminSetupLink = available;
      },
      error: () => {
        this.showSuperAdminSetupLink = false;
      },
    });
  }
}
